/**
 * Briefing API client — thin wrappers over authFetch for the guided onboarding.
 * Routes live under /api/internal/implementation.
 */
import { authGet, authPut, authPost } from "@/utils/api";

const BASE = "/api/internal/implementation";
const INTEGRATIONS = "/api/internal/integrations";

/** Unwrap a Response, throwing an Error carrying `.status`/`.blockers` on failure. */
async function unwrap(res) {
  if (!res.ok) {
    const payload = await res.json().catch(() => ({}));
    const err = new Error(payload.error || "Erro na requisição.");
    err.status = res.status;
    err.blockers = payload.blockers;
    throw err;
  }
  return res.json();
}

/** Ensure the ImplementationProject exists (auto-creates) and return it. */
export async function ensureProject(clientId) {
  return unwrap(await authGet(`${BASE}/project/${clientId}`));
}

export async function fetchBriefing(clientId) {
  return unwrap(await authGet(`${BASE}/project/${clientId}/briefing`));
}

export async function saveBriefing(clientId, patch) {
  return unwrap(await authPut(`${BASE}/project/${clientId}/briefing`, patch));
}

export async function saveIntegrations(clientId, patch) {
  return unwrap(await authPut(`${BASE}/project/${clientId}/briefing/integrations`, patch));
}

export async function completeBriefing(projectId) {
  return unwrap(await authPost(`${BASE}/project/${projectId}/briefing/complete`, {}));
}

export async function fetchPracticeAreas() {
  return unwrap(await authGet(`${BASE}/briefing/practice-areas`));
}

export async function fetchQualificationQuestions(areaIds = []) {
  const qs = encodeURIComponent(areaIds.join(","));
  return unwrap(await authGet(`${BASE}/briefing/library/qualification-questions?areas=${qs}`));
}

export async function fetchServiceMenuOptions(provider = "none") {
  return unwrap(
    await authGet(`${BASE}/briefing/service-menu-options?provider=${encodeURIComponent(provider)}`),
  );
}

// --- Account directory (Bloco 3 — lead recipient: person or department) ---

export async function fetchAccountUsers() {
  const data = await unwrap(await authGet(`/api/users`));
  return data.users || data || [];
}

export async function fetchDepartments() {
  const data = await unwrap(await authGet(`/api/departments`));
  return Array.isArray(data) ? data : data.departments || [];
}

// --- Go-live scheduling (Bloco 7) ---

export async function fetchSlots(date) {
  return unwrap(await authGet(`${BASE}/scheduling/slots?date=${encodeURIComponent(date)}`));
}

export async function preReserveSlot(date, time, sessionId) {
  return unwrap(await authPost(`${BASE}/scheduling/pre-reserve`, { date, time, sessionId }));
}

export async function confirmGoLive(projectId, payload) {
  return unwrap(await authPost(`${BASE}/project/${projectId}/briefing/go-live`, payload));
}

// --- Juridical connection (Bloco J) reuses the existing integration endpoints ---

export async function validateAdvboxToken(token) {
  return unwrap(await authPost(`${INTEGRATIONS}/advbox/validate-token`, { token }));
}

export async function validateProjurisCredentials({ username, password, domain }) {
  return unwrap(
    await authPost(`${INTEGRATIONS}/projuris/validate-credentials`, {
      username,
      password,
      domain,
    }),
  );
}

export async function validateControljusCredentials({ username, password }) {
  return unwrap(
    await authPost(`${INTEGRATIONS}/controljus/validate-credentials`, {
      username,
      password,
    }),
  );
}

export async function validateAstreaCredentials({ username, password }) {
  return unwrap(
    await authPost(`${INTEGRATIONS}/astrea/validate-credentials`, {
      username,
      password,
    }),
  );
}

export async function validateFazToken(token) {
  return unwrap(await authPost(`${INTEGRATIONS}/faz/validate-token`, { token }));
}

/**
 * Persist juridical credentials via the generic integration-create route.
 * @param {"advbox"|"projuris"|"controljus"|"astrea"|"faz"} provider
 * @param {object} credentials advbox/faz: { token }; projuris: { username, password, domain };
 *   controljus/astrea: { username, password }
 * @returns the created/updated ClientIntegration (carries `id`)
 */
export async function connectJuridical(clientId, provider, credentials) {
  return unwrap(await authPost(`${INTEGRATIONS}/${clientId}/${provider}`, credentials));
}
